import { useState } from 'react';

/**
 * Interface representing the values of the login form.
 */
interface FormValues {
  cedula: string;        // The identification number (cedula) of the user.
  password: string;      // The password entered by the user.
  userDocType: string;   // The document type of the user (e.g., CC, TI).
  country: string;       // The country of the user (e.g., CO for Colombia).
}

/**
 * Custom hook for managing login form state.
 *
 * This hook encapsulates the state and behavior of a login form.
 * It provides a way to manage form input values, handle input changes,
 * and update the form state.
 *
 * @returns {Object} An object containing:
 * @returns {FormValues} formData - The current values of the login form.
 * @returns {function} handleChange - A function to handle changes in form input fields.
 * @returns {function} setFormData - A function to update the form data directly.
 *
 * @example
 * const MyLoginComponent = () => {
 *   const { formData, handleChange, setFormData } = useLoginForm();
 *
 *   const handleSubmit = (e: React.FormEvent) => {
 *     e.preventDefault();
 *     // Submit the form data
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <input
 *         type="text"
 *         name="cedula"
 *         value={formData.cedula}
 *         onChange={handleChange}
 *         placeholder="Cedula"
 *       />
 *       <input
 *         type="password"
 *         name="password"
 *         value={formData.password}
 *         onChange={handleChange}
 *         placeholder="Password"
 *       />
 *       <button type="submit">Login</button>
 *     </form>
 *   );
 * };
 */
const useLoginForm = () => {
  const [formData, setFormData] = useState<FormValues>({
    userDocType: 'CC',
    cedula: '',
    country: 'CO',
    password: '',
  });

  /**
   * Handles changes in the form input fields.
   *
   * @param e - The event object representing the input change event.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  return {
    formData,
    handleChange,
    setFormData,
  };
};

export default useLoginForm;
